import { MouseEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Button from '@material-ui/core/Button';
import { setError } from 'src/redux/actions/errorsActions';
import { t } from 'src/lib/language';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { FORM } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import Form from 'src/components/UI/Form';
import BottomRow from 'src/components/UI/BottomRow';
import { MODALS } from 'src/models/ModalModel';
import Row from 'src/components/UI/Row';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { closeModal, openModal } from 'src/redux/actions/modalActions';
import SubmenuLink from 'src/components/UI/SubmenuLink';
import AmbassadorValidator from 'src/validations/AmbassadorValidator';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import Title from 'src/components/UI/Title';
import Header from 'src/components/UI/Header';
import { putAmbassadorAction } from 'src/redux/actions/ambassadorActions';
import { AmbassadorPayload, FundraisingModel } from 'src/models/FundraisingModel';
import { getS3URL } from 'src/lib/S3File';
import ContactModel from 'src/models/ContactModel';
import { getContactByIdAction } from 'src/redux/actions/contactActions';
import { getFundraisingAction } from 'src/redux/actions/fundraisingAction';
import { FundraisingTypes } from 'src/constants/FundraisingTypes';
import { setSelections } from 'src/redux/actions/selectionActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';

const FundraisingEditAmbassadorForm = () => {
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language);
  const [ambassador, fundraisingSlug, fundraisingId] = useSelector(
    (state: Store) => state.modals[MODALS.editAmbassador]?.payload ?? [null, null],
  );
  const organisation = useSelector((state: Store) => state.currentOrganisation.id);

  const [ambassadorState, setAmbassadorState] = useState({
    ...ambassador,
  });

  const contact = useSelector((state: Store) => {
    if (!state.contacts[ambassador.contact_id]) {
      return null;
    }
    return state.contacts[ambassador.contact_id].contact as ContactModel;
  });

  const fundraising = useSelector((state: Store) => {
    if (!state.fundraisings[fundraisingId]) {
      return null;
    }
    return state.fundraisings[fundraisingId] as FundraisingModel;
  });

  const getPhone = () => contact?.phone_mobile ?? contact?.phone_office ?? contact?.phone_home ?? 'n/a';

  const getEmail = () => contact?.email ?? 'n/a';

  useEffect(() => {
    if (!contact) {
      dispatch(getContactByIdAction(ambassador.contact_id));
    }
  }, []);

  useEffect(() => {
    if (!fundraising) {
      dispatch(getFundraisingAction(fundraisingId));
    }
  }, []);

  const [wordCount, setWordCount] = useState(0);

  const form = FORM.fundraising_ambassador;

  useEffect(() => {
    dispatch(setError({ [form]: {} }));
  }, []);

  const onValueChange = (value: any, field: string) => {
    if (field === 'name') {
      setAmbassadorState({
        ...ambassadorState,
        slug: value.toLowerCase()
          .replace(' ', '-')
          .replace(/[^0-9a-z-]/gi, ''),
        [field]: value,
      });
      return;
    }
    if (field === 'slug') {
      const slug = value.toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      setAmbassadorState({
        ...ambassadorState,
        complete_link: `https://perfectdeed.ca/${fundraisingSlug}/${slug}`,
        [field]: slug,
      });
      return;
    }
    if (field === 'message') {
      if (value.length > 512) {
        return;
      }
      setWordCount(value.length);
    }
    setAmbassadorState({
      ...ambassadorState,
      [field]: value,
    });
  };

  const onUploadImage = () => {
    dispatch(openModal({
      modal: MODALS.uploadImage,
      payload: {
        path: `${organisation}/events/fundraising-${fundraisingId}`,
        size: {
          width: 250,
          height: 250,
          ratio: 1,
        },
        name: '',
        onUpload: (url: string) => onValueChange(`${getS3URL()}${url}`, 'avatar'),
      },
    }));
  };

  const handleEditContact = () => {
    dispatch(setSelections({
      entity: ENTITIES.contacts,
      selections: [ambassador.contact_id],
    }));
    dispatch(openModal({ modal: MODALS.editContacts }));
  };

  const handleClose = () => {
    dispatch(
      closeModal({ modal: MODALS.editAmbassador }),
    );
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [form]: {} }));
    try {
      AmbassadorValidator().validateSync(ambassadorState, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Validation error',
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [form]: errorBag }));
      return;
    }
    dispatch(setError({ [form]: {} }));

    ambassadorState.phone = ambassadorState.phone ?? 'n/a';
    ambassadorState.email = ambassadorState.email ?? 'n/a';

    dispatch(putAmbassadorAction(ambassadorState as AmbassadorPayload));
    dispatch(closeModal(MODALS.addAmbassador));
  };

  const getMaxWord = () => `${wordCount}/512 characters`;

  const getUrl = () => {
    if (ambassadorState.complete_link) {
      return ambassadorState.complete_link;
    }
    if (!fundraisingSlug) {
      return null;
    }
    const type = fundraising?.fundraisingInfo?.type ?? null;
    const path = `${fundraisingSlug ?? ''}/${ambassadorState.slug ?? ''}/`;
    const directory = type === FundraisingTypes.standard ? 'events/' : '';
    return `https://perfectdeed.ca/${directory}${path}`;
  };

  const formatBoolean = (booleanValue: number) => (
    <span style={{
      height: '24px',
    }}>
      {booleanValue
        ? <CheckIcon fontSize="small" color="primary" />
        : <CloseIcon fontSize="small" color="disabled" />}
    </span>
  );

  return (
    <>
      <Header height="56px">
        <Row>
          <Title>
            Edit Ambassador
          </Title>
          <Button
            style={{ marginLeft: 'auto' }}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClose}>
            {t(lang, 'misc.close')}
          </Button>
        </Row>
      </Header>
      <Form>
        <Row>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignItems: 'start',
            height: '290px',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{
                fontSize: '24px',
                fontFamily: 'Qanelas-Bold',
              }}>
                {ambassadorState.name}
              </span>
            </div>
            <div>
              email: {getEmail()}<br />
              phone: {getPhone()}<br />
              <SubmenuLink onClick={handleEditContact} color="#0A4DF2">
                {t(lang, 'menus.edit_contact')}
              </SubmenuLink>
            </div>
            <div style={{
              marginTop: 'auto',
              marginBottom: '0px',
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
            }}>
              <div style={{ width: '50%' }}>
                <FormCheckbox
                  label="Notify by email"
                  name="notify_by_email"
                  onChange={onValueChange}
                  checked={!!ambassadorState.notify_by_email} />
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}>
                  Email invitation sent: {formatBoolean(ambassadorState.invitation_email_sent)}
                </div>
              </div>
              <div style={{ width: '50%' }}>
                <FormCheckbox
                  label="Notify by SMS"
                  name="notify_by_sms"
                  onChange={onValueChange}
                  checked={!!ambassadorState.notify_by_sms} />
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}>
                  Text invitation sent: {formatBoolean(ambassadorState.invitation_sms_sent)}
                </div>
              </div>
            </div>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '250px',
            height: '290px',
          }}>
            <div style={{
              height: '250px',
              width: '250px',
            }}>
              {ambassadorState.avatar && (
                <img
                  src={ambassadorState.avatar}
                  alt="" />
              )}
            </div>
            <div>
              <SubmenuLink color="#0A4DF2" onClick={onUploadImage}>Upload Image</SubmenuLink>
            </div>
            <div style={{
              width: '100%',
              marginTop: '-10px',
            }}>
              <span style={{
                fontSize: '12px',
                fontStyle: 'italic',
              }}>
                250px X 250px | max 50Mb | (png or jpg)
              </span>
            </div>
          </div>
        </Row>
        <FormTextField
          form={form}
          label="Ambassador Name"
          name="name"
          onChange={onValueChange}
          value={ambassadorState.name}
          margin="dense"
          required />
        <FormTextField
          form={form}
          label="Goal"
          name="goal"
          onChange={onValueChange}
          value={ambassadorState.goal}
          margin="dense"
          required />
        <FormTextField
          form={form}
          label="Ambassador Shortlink"
          name="slug"
          onChange={onValueChange}
          value={ambassadorState.slug}
          margin="dense"
          required />
        <div style={{
          fontSize: '12px',
          color: '#333',
          marginLeft: '2px',
        }}>
          <b>Url:&nbsp;</b>{getUrl()}
        </div>
        {ambassadorState.complete_link_secret && (
          <div style={{
            fontSize: '12px',
            color: '#333',
            marginLeft: '2px',
          }}>
            <b>Secret url:&nbsp;</b> {ambassadorState.complete_link_secret}
          </div>
        )}
        <FormTextField
          form={form}
          label="Write your message"
          name="message"
          onChange={onValueChange}
          multiline
          minRows={5}
          maxRows={5}
          value={ambassadorState.message} />
        <div style={{
          fontSize: '12px',
          marginTop: '-4px',
          color: '#333',
          marginLeft: '2px',
        }}>
          {getMaxWord()}
        </div>
        <FormTextField
          form={form}
          label="Your message video link"
          name="video_link"
          onChange={onValueChange}
          value={ambassadorState.video_link}
          margin="dense" />
        <BottomRow>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '12px' }}
            onClick={handleSubmit}
            key="submit-button">
            {t(lang, 'misc.save')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '12px' }}
            onClick={handleClose}
            key={`submit-button-group-${ambassadorState.id}`}>
            close
          </Button>
        </BottomRow>
      </Form>
    </>
  );
};

export default FundraisingEditAmbassadorForm;
//
